// client/src/components/layout/Sidebar.js
import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  BarChart3, ChevronDown, Clock, Users, Building2, DollarSign, FileText, Settings, Send, Archive, UserCircle, LogOut, Landmark, TrendingUp, ArrowUpDown, Upload, Download, Activity, Receipt, Banknote
} from 'lucide-react';
import LanguageSelector from './LanguageSelector';
import '../../styles/Sidebar.css';

const Sidebar = forwardRef((props, ref) => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isExpanded, setIsExpanded] = useState(!isMobile);
  const [pendingUsersCount, setPendingUsersCount] = useState(0);
  const [creditAssignmentEnabled, setCreditAssignmentEnabled] = useState(false);
  const [openMenus, setOpenMenus] = useState({});
  
  // Funzione per gestire il toggle dei sottomenù
  const toggleSubmenu = (menuKey) => {
    setOpenMenus(prev => ({
      ...prev,
      [menuKey]: !prev[menuKey]
    }));
  };

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      setIsExpanded(!mobile);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Fetch pending users count for admin
  useEffect(() => {
    if (user && user.role === 'admin') {
      const fetchPendingUsersCount = async () => {
        try {
          const res = await axios.get('/api/admin/pending-users');
          setPendingUsersCount(res.data.pendingUsers.length);
        } catch (err) {
          console.error('Error fetching pending users:');
        }
      };
      
      fetchPendingUsersCount();
      
      // Refresh count every minute
      const interval = setInterval(fetchPendingUsersCount, 60000);
      return () => clearInterval(interval);
    }
  }, [user]);
  
  // Cessione del credito: mostra la sezione solo alle aziende abilitate dall'admin
  useEffect(() => {
    if (user && user.role === 'company') {
      axios.get('/api/credit-assignment/access')
        .then((res) => setCreditAssignmentEnabled(res.data.enabled === true))
        .catch(() => setCreditAssignmentEnabled(false));
    } else {
      setCreditAssignmentEnabled(false);
    }
  }, [user]);

  // Cleanup body class on unmount
  useEffect(() => {
    return () => {
      document.body.classList.remove('sidebar-open');
    };
  }, []);
  
  // Toggle sidebar expansion on mobile con gestione body scroll
  const toggleSidebar = () => {
    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);
    
    // Prevent body scroll when sidebar is open on mobile
    if (isMobile) {
      if (newExpanded) {
        document.body.classList.add('sidebar-open');
      } else {
        document.body.classList.remove('sidebar-open');
      }
    }
  };
  
  // NUOVO: Esponi la funzione toggleSidebar tramite ref
  useImperativeHandle(ref, () => ({
    toggleSidebar
  }));
  
  // Close sidebar when clicking on links (mobile only)
  const handleLinkClick = () => {
    if (isMobile && isExpanded) {
      setIsExpanded(false);
      document.body.classList.remove('sidebar-open');
    }
  };

  // Handle logout con chiusura sidebar
  const handleLogout = () => {
    logout();
    navigate('/login');
    // Close sidebar on mobile after logout
    if (isMobile) {
      setIsExpanded(false);
      document.body.classList.remove('sidebar-open');
    }
  };
  
  // Define navigation links based on user role
  const getNavLinks = () => {
    if (user.role === 'admin') {
      return [
        { to: '/admin/dashboard', textKey: 'sidebar.dashboard', icon: BarChart3 },
        { to: '/admin/pending-users', textKey: 'sidebar.pendingRegistrations', icon: Clock, badge: pendingUsersCount },
        { to: '/admin/users', textKey: 'sidebar.users', icon: Users },
        { to: '/admin/organizations', textKey: 'sidebar.organizations', icon: Building2 },
        { to: '/admin/requests', textKey: 'sidebar.liquidityRequests', icon: DollarSign },
        { to: '/admin/bids', textKey: 'sidebar.bids', icon: FileText },
        { to: '/admin/markets', textKey: 'sidebar.marketsManagement', icon: TrendingUp },
        {
          textKey: 'sidebar.creditAssignment',
          icon: Banknote,
          isSubmenu: true,
          key: 'adminCreditAssignment',
          submenuItems: [
            { to: '/admin/credit-assignment/companies', textKey: 'sidebar.caCompanies', icon: Building2 },
            { to: '/admin/credit-assignment/requests', textKey: 'sidebar.caRequests', icon: FileText }
          ]
        },
        { to: '/admin/profile', textKey: 'sidebar.profileSettings', icon: Settings }
      ];
    } else if (user.role === 'bank') {
    const isReferral = user.isReferral === true;
    const bankLinks = [
      { to: '/bank/dashboard', textKey: 'sidebar.dashboard', icon: BarChart3 }
    ];

    if (isReferral) {
      // Banche referral: accesso ai Crediti IVA, quindi Nuove Offerte e Offerte Passate
      // sono sottomenù con Depositi, Hot Money e Crediti IVA
      bankLinks.push({
        textKey: 'sidebar.newBids',
        icon: Send,
        isSubmenu: true,
        key: 'newBidsBank',
        submenuItems: [
          { to: '/bank/open-requests/allocation', textKey: 'sidebar.allocation', icon: Upload },
          { to: '/bank/open-requests/hot-money', textKey: 'sidebar.hotMoney', icon: Download },
          { to: '/bank/vat-credit/open-requests', textKey: 'sidebar.vatCredit', icon: Receipt }
        ]
      });
      bankLinks.push({
        textKey: 'sidebar.pastBids',
        icon: Archive,
        isSubmenu: true,
        key: 'pastBidsBank',
        submenuItems: [
          { to: '/bank/past-bids/allocation', textKey: 'sidebar.allocations', icon: Upload },
          { to: '/bank/past-bids/hot-money', textKey: 'sidebar.hotMoney', icon: Download },
          { to: '/bank/vat-credit/past-bids', textKey: 'sidebar.vatCredit', icon: Receipt }
        ]
      });
    } else {
      // Banche non referral: niente Crediti IVA, viste combinate depositi + hot money
      bankLinks.push({ to: '/bank/open-requests', textKey: 'sidebar.newBids', icon: Send });
      bankLinks.push({ to: '/bank/past-bids', textKey: 'sidebar.pastBids', icon: Archive });
      bankLinks.push({ to: '/bank/team', textKey: 'sidebar.teamManagement', icon: Users });
    }

    bankLinks.push({ to: '/bank/profile', textKey: 'sidebar.profileSettings', icon: UserCircle });

    return bankLinks;

} else { // company
    const isPaying = user?.organization?.isPaying !== false;
    // Crediti IVA (VAT Credit): disponibile SOLO se isPaying === true (il backend non accetta undefined/legacy come per allocation/hot-money)
    const isPayingStrict = user?.organization?.isPaying === true;
    const companyLinks = [
      { to: '/company/dashboard', textKey: 'sidebar.dashboard', icon: BarChart3 }
    ];

    // Prodotti: ognuno con il proprio sottomenu Nuova richiesta / Richieste.
    // Depositi, Denaro Caldo e Crediti IVA hanno anche la propria dashboard di dettaglio: il clic la apre ed espande il sottomenu.
    companyLinks.push({ isSection: true, textKey: 'sidebar.sectionProducts' });
    const depositsSubmenuItems = [
      { to: '/company/new-request/allocation', textKey: 'sidebar.newRequest', icon: Send },
      { to: '/company/past-requests/allocation', textKey: 'sidebar.pastRequests', icon: Archive }
    ];
    // Analisi (Performance + Analytics dei soli depositi): la Performance è riservata ai paganti
    if (isPaying) {
      depositsSubmenuItems.push({ to: '/company/deposits/analysis', textKey: 'sidebar.analysis', icon: Activity });
    }
    companyLinks.push({
      to: '/company/deposits',
      textKey: 'sidebar.allocations',
      icon: Upload,
      isSubmenu: true,
      key: 'deposits',
      submenuItems: depositsSubmenuItems
    });
    if (isPaying) {
      companyLinks.push({
        to: '/company/hot-money',
        textKey: 'sidebar.hotMoney',
        icon: Download,
        isSubmenu: true,
        key: 'hotMoney',
        submenuItems: [
          { to: '/company/new-request/hot-money', textKey: 'sidebar.newRequest', icon: Send },
          { to: '/company/past-requests/hot-money', textKey: 'sidebar.pastRequests', icon: Archive },
          // Analisi (Performance + Analytics del solo denaro caldo), come per i depositi
          { to: '/company/hot-money/analysis', textKey: 'sidebar.analysis', icon: Activity }
        ]
      });
    }
    if (isPayingStrict) {
      companyLinks.push({
        to: '/company/vat-credit',
        textKey: 'sidebar.vatCredit',
        icon: Receipt,
        isSubmenu: true,
        key: 'vatCredit',
        submenuItems: [
          { to: '/company/vat-credit/new-request', textKey: 'sidebar.newRequest', icon: Send },
          { to: '/company/vat-credit/past-requests', textKey: 'sidebar.pastRequests', icon: Archive }
        ]
      });
    }

    // Cessione del credito: voce separata, visibile (sia paganti che non) solo se abilitata dall'admin
    if (creditAssignmentEnabled) {
      companyLinks.push({ isSection: true });
      companyLinks.push({
        textKey: 'sidebar.creditAssignment',
        icon: Banknote,
        isSubmenu: true,
        key: 'creditAssignment',
        submenuItems: [
          { to: '/company/credit-assignment/clients', textKey: 'sidebar.caClients', icon: Users },
          { to: '/company/credit-assignment/new-request', textKey: 'sidebar.caNewRequest', icon: Send },
          { to: '/company/credit-assignment/requests', textKey: 'sidebar.caRequests', icon: Archive }
        ]
      });
    }

    // Gestione: Mercati e Banche (solo paganti), Team, Impostazioni
    companyLinks.push({ isSection: true, textKey: 'sidebar.sectionManagement' });
    if (user?.organization?.isPaying !== false) {
      companyLinks.push({ to: '/company/markets', textKey: 'sidebar.markets', icon: ArrowUpDown });
    }
    companyLinks.push({ to: '/company/team', textKey: 'sidebar.teamManagement', icon: Users });
    if (user?.organization?.isPaying !== false) {
      companyLinks.push({ to: '/company/bank-management', textKey: 'sidebar.bankManagement', icon: Landmark });
    }
    companyLinks.push({ to: '/company/profile', textKey: 'sidebar.profileSettings', icon: UserCircle });

    return companyLinks;
  }
};
  
  const navLinks = getNavLinks();

  // Tiene aperto il sottomenu della pagina corrente (es. Depositi su /company/new-request/allocation)
  useEffect(() => {
    const keysToOpen = navLinks
      .filter(link => link.isSubmenu && (link.to === pathname || link.submenuItems.some(item => item.to === pathname)))
      .map(link => link.key);
    if (keysToOpen.length > 0) {
      setOpenMenus(prev => ({ ...prev, ...Object.fromEntries(keysToOpen.map(key => [key, true])) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <>
      {/* Mobile toggle button */}
      {isMobile && (
        <button 
          className="sidebar-toggle" 
          onClick={toggleSidebar}
          aria-label={isExpanded ? t('sidebar.closeMenu') : t('sidebar.openMenu')}
        >
          {isExpanded ? '✕' : '☰'}
        </button>
      )}
      
      <div className={`sidebar ${isExpanded ? 'expanded' : 'collapsed'}`}>
        <div className="sidebar-content">
          <div className="logo">
            <img 
              src="/logoBlu.png" 
              alt="Euracle Logo" 
              className="main-logo"
            />
          </div>
          
<ul className="nav-links">
  {navLinks.map((link, index) => {
    const IconComponent = link.icon;
    const translatedText = t(link.textKey);
    
    // Intestazione di sezione (senza testo = solo separatore)
    if (link.isSection) {
      return (
        <li key={index} className="nav-section">
          {isExpanded && link.textKey && <span className="nav-section-label">{translatedText}</span>}
        </li>
      );
    }

    // Se è un sottomenù
    if (link.isSubmenu) {
      const isOpen = openMenus[link.key];
      const hasActiveChild = link.submenuItems.some(item => pathname === item.to);
      // Sottomenu con pagina propria (es. Depositi): il clic apre la pagina ed espande il sottomenu;
      // se si è già sulla pagina con il sottomenu aperto, il clic lo richiude
      const isParentActive = link.to && pathname === link.to;
      const handleParentClick = () => {
        if (link.to && isParentActive && isOpen && isExpanded) {
          toggleSubmenu(link.key);
        } else if (link.to) {
          setOpenMenus(prev => ({ ...prev, [link.key]: true }));
          navigate(link.to);
          handleLinkClick();
        } else if (isExpanded) {
          toggleSubmenu(link.key);
        }
      };
      const handleArrowClick = (e) => {
        e.stopPropagation();
        toggleSubmenu(link.key);
      };

      return (
        <li key={index} className="nav-item nav-item-submenu">
          <div
            className={`nav-link-submenu ${isParentActive ? 'active' : ''} ${hasActiveChild ? 'has-active-child' : ''}`}
            onClick={handleParentClick}
            title={!isExpanded ? translatedText : ''}
          >
            <span className="nav-icon">
              <IconComponent size={20} />
            </span>
            {isExpanded && (
              <>
                <span className="nav-text">{translatedText}</span>
                <span
                  className={`submenu-arrow ${isOpen ? 'open' : ''}`}
                  onClick={link.to ? handleArrowClick : undefined}
                >
                  <ChevronDown size={14} />
                </span>
              </>
            )}
          </div>
          
          {isExpanded && isOpen && (
            <ul className="submenu">
              {link.submenuItems.map((subItem, subIndex) => {
                const SubIconComponent = subItem.icon;
                const subTranslatedText = t(subItem.textKey);
                
                return (
                  <li 
                    key={subIndex}
                    className={`submenu-item ${pathname === subItem.to ? 'active' : ''}`}
                  >
                    <Link to={subItem.to} onClick={handleLinkClick}>
                      <span className="nav-icon">
                        <SubIconComponent size={18} />
                      </span>
                      <span className="nav-text">{subTranslatedText}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </li>
      );
    }
    
    // Link normale
    return (
      <li 
        key={index} 
        className={`nav-item ${pathname === link.to ? 'active' : ''}`}
        title={!isExpanded ? translatedText : ''}
      >
        <Link to={link.to} onClick={handleLinkClick}>
          <span className="nav-icon">
            <IconComponent size={20} />
          </span>
          {isExpanded && <span className="nav-text">{translatedText}</span>}
          {link.badge > 0 && (
            <span className="badge-notification">{link.badge}</span>
          )}
        </Link>
      </li>
    );
  })}
</ul>

          {/* Mobile User Controls Section */}
          {isMobile && isExpanded && (
            <div className="sidebar-mobile-controls">
              {/* User Info */}
              <div className="mobile-user-info">
                <div className="mobile-user-name">
                  <UserCircle size={20} />
                  <span>{user?.organization?.name || user?.username || 'Admin'}</span>
                </div>
              </div>

              {/* Language Selector - Only for non-admin users */}
              {user?.role !== 'admin' && (
                <div className="mobile-language-section">
                  <div className="mobile-language-wrapper">
                    <LanguageSelector />
                  </div>
                </div>
              )}

              {/* Logout Section */}
              <div className="mobile-logout-section">
                <button onClick={handleLogout} className="mobile-logout-btn">
                  <LogOut size={20} />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Overlay for mobile */}
      {isMobile && isExpanded && (
        <div className="sidebar-overlay" onClick={toggleSidebar}></div>
      )}
    </>
  );
});

// Aggiungi displayName per debugging
Sidebar.displayName = 'Sidebar';

export default Sidebar;